import type {
  MarketDataPreferenceBatchRequest,
  MarketDataPreferenceBatchResponse,
  MarketDataPreferenceSkipBatchRequest,
  MarketDataPreferenceSkipRequest,
} from "../types/instrumentSetup";
import type {
  MarketDataPreferenceRequest,
  ResolveInstrumentRequest,
  ResolveInstrumentResponse,
} from "../types/instruments";
import {
  MarketDataUnresolvedError,
  parseMarketDataUnresolvedDetail,
} from "../utils/marketDataResolution";
import { parseApiErrorBody } from "../utils/parseApiError";
import { authenticatedFetch } from "./apiClient";

export class InstrumentsApiError extends Error {
  status: number;
  detail?: string;
  body?: unknown;

  constructor(message: string, status: number, detail?: string, body?: unknown) {
    super(message);
    this.name = "InstrumentsApiError";
    this.status = status;
    this.detail = detail;
    this.body = body;
  }
}

async function parseOrThrow(response: Response, fallbackMessage: string): Promise<unknown> {
  if (response.ok) return await response.json();

  if (response.status === 401) {
    throw new InstrumentsApiError("Session expired. Please log in again.", 401);
  }

  const error = await response.json().catch(() => ({}));
  if (response.status === 422) {
    const unresolved = parseMarketDataUnresolvedDetail(error);
    if (unresolved) {
      throw new MarketDataUnresolvedError(unresolved, 422);
    }
  }
  const detail =
    parseApiErrorBody(
      error && typeof error === "object" ? (error as Record<string, unknown>) : {},
      fallbackMessage
    ).message;
  throw new InstrumentsApiError(detail || fallbackMessage, response.status, detail, error);
}

export async function resolveInstrument(
  body: ResolveInstrumentRequest
): Promise<ResolveInstrumentResponse> {
  const response = await authenticatedFetch("/instruments/resolve", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return (await parseOrThrow(response, `Failed to resolve instrument: ${response.status}`)) as ResolveInstrumentResponse;
}

export async function setMarketDataPreference(
  body: MarketDataPreferenceRequest
): Promise<void> {
  const response = await authenticatedFetch("/instruments/market-data-preference", {
    method: "PUT",
    body: JSON.stringify(body),
  });
  await parseOrThrow(response, `Failed to save market data preference: ${response.status}`);
}

export async function setMarketDataPreferencesBatch(
  body: MarketDataPreferenceBatchRequest
): Promise<MarketDataPreferenceBatchResponse> {
  const response = await authenticatedFetch("/instruments/market-data-preferences/batch", {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return (await parseOrThrow(
    response,
    `Failed to save market data preferences: ${response.status}`
  )) as MarketDataPreferenceBatchResponse;
}

/** Skip one symbol — clears pending without OHLC prefs (charts still 422 until a real save). */
export async function skipMarketDataPreference(
  body: MarketDataPreferenceSkipRequest
): Promise<void> {
  const response = await authenticatedFetch("/instruments/market-data-preference/skip", {
    method: "PUT",
    body: JSON.stringify(body),
  });
  await parseOrThrow(response, `Failed to skip market data preference: ${response.status}`);
}

/** Skip many symbols in one call. */
export async function skipMarketDataPreferencesBatch(
  body: MarketDataPreferenceSkipBatchRequest
): Promise<void> {
  const response = await authenticatedFetch("/instruments/market-data-preferences/skip-batch", {
    method: "PUT",
    body: JSON.stringify(body),
  });
  await parseOrThrow(response, `Failed to skip market data preferences: ${response.status}`);
}
